import { ReactNode, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { getDepartmentList } from "../redux/slices/employee";
import { dispatch, useSelector } from "../redux/store";
import {
  MoreHorizontal, MoreVertical, Eye, Trash2, X, FileText, Download,
  ChevronLeft, ChevronRight, Save, Send, IndianRupee, Check, Filter as FilterIcon,
  Clock,
} from "lucide-react";
import { salaryApi, SalaryRecord, DailyBreakdown, RateType } from "../api/salary";
import FilterDropdown from "../components/FilterDropdown";
import ConfirmDialog, { type ConfirmState } from "../components/ConfirmDialog";
import PayDeductionNote from "../components/PayDeductionNote";
import PayDeductionDetails from "../components/PayDeductionDetails";
import api from "../api/api";
import { downloadSalarySlip, getMyEmployeeProfile, viewSalarySlip } from "../api/employees";
import { useAccess } from "../contexts/AccessContext";
import {
  buildApprovalStatusMap,
  getPreviousPayrollPeriod,
  statusAfterSendForApproval,
  successfulSendForApprovalIds,
} from "./payrollApprovalLogic";
import { dayStatusMeta } from "../utils/dayStatus";
import { formatHoursMinutes } from "../utils/hoursFormat";
import { toast } from "sonner";
import PersonAvatar from "../components/people/PersonAvatar";

type Employee = {
  id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  employeeID?: string;
  email?: string;
  hire_date?: string | null;
  profile_photo_url?: string | null;
  is_active?: boolean;
  employment_status?: string | null;
};

type PayrollRecordProps = {
  headerActions?: ReactNode;
  includeCurrentUserInEmployeeList?: boolean;
};

const ALL_DEPTS = "__ALL__";
const ALL_EMPS = "__ALL_EMP__";
const PAGE_SIZE = 10;
const PAYROLL_GREEN = "bg-[#0f2d1f] text-white hover:bg-[#123b29]";

// The calculation methods a row can be computed with. Tracked per employee
// client-side so the "Calculation Method" badge reflects the last method applied.
// Two no-deduction flavours: plain "noded" bypasses attendance and shows the
// month's days; "noded_att" pays the full month too but keeps the clocked
// attendance (present / effective days, overtime, lates) on the record.
type MethodKey = "attendance" | "noded" | "noded_att" | "system";
const METHOD_META: Record<MethodKey, { label: string; cls: string }> = {
  attendance: { label: "Attendance", cls: "bg-indigo-100 text-indigo-700" },
  noded: { label: "No Deduction", cls: "bg-orange-100 text-orange-700" },
  noded_att: { label: "No Deduction + Attendance", cls: "bg-amber-100 text-amber-800" },
  system: { label: "Workflow", cls: "bg-emerald-100 text-emerald-700" },
};
// The server's `method` on a calculated row → badge key.
const SERVER_METHOD: Record<string, MethodKey> = {
  attendance: "attendance",
  no_deduction: "noded",
  no_deduction_attendance: "noded_att",
};

// Whether a day is valued by hours worked (hourly) or as a flat per-day share of
// the monthly salary. Company-wide in Settings → Leaves → Leave Policy; this page
// can override it per employee (persisted server-side in `salary_settings`).
const RATE_TYPE_META: Record<RateType, { label: string; cls: string }> = {
  hourly: { label: "Hourly", cls: "bg-sky-100 text-sky-700" },
  monthly: { label: "Monthly", cls: "bg-violet-100 text-violet-700" },
};

// Salary approval lifecycle → Status badge. Derived from the record's flags:
// approved → Approved; else sent_for_approval → Sent for Approval; else Pending.
type StatusKey = "approved" | "sent" | "pending";
const STATUS_META: Record<StatusKey, { label: string; cls: string }> = {
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  sent: { label: "Sent for Approval", cls: "bg-blue-100 text-blue-700" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
};
const statusOf = (r: SalaryRecord): StatusKey =>
  r.approved ? "approved" : r.sent_for_approval ? "sent" : "pending";

// The bulk-calc endpoint returns a mix of real salary records and per-employee
// error objects ({ employee_id, success:false, error }) when a calc fails.
// Keep only genuine salary records so the table never renders blank ₹0 rows.
const filterOkResults = (results: any[]): SalaryRecord[] =>
  (results || []).filter((r) => r && r.success !== false && r.name) as SalaryRecord[];

const normaliseEmployeeList = (employees: Employee[], selfProfile: any): Employee[] => {
  if (!selfProfile?.has_employee_record || !selfProfile.id) return employees;
  if (employees.some((emp) => emp.id === selfProfile.id)) return employees;

  return [
    ...employees,
    {
      id: selfProfile.id,
      first_name: selfProfile.first_name || selfProfile.name || "Me",
      middle_name: selfProfile.middle_name,
      last_name: selfProfile.last_name || "",
      employeeID: selfProfile.employeeID,
      email: selfProfile.email,
      hire_date: selfProfile.hire_date,
      profile_photo_url: selfProfile.profile_photo_url,
      is_active: selfProfile.is_active,
      employment_status: selfProfile.employment_status,
    },
  ];
};

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toPayrollPeriodIndex = (year: string | number, month: string | number) =>
  Number(year) * 12 + Number(month);

const hireDateParts = (hireDate?: string | null) => {
  if (!hireDate) return null;
  const [year, month] = hireDate.split("-");
  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  if (!parsedYear || !parsedMonth) return null;
  return { year: parsedYear, month: parsedMonth };
};

const isBeforeJoiningMonth = (
  year: string | number,
  month: string | number,
  hireDate?: string | null
) => {
  const joining = hireDateParts(hireDate);
  if (!joining) return false;
  return toPayrollPeriodIndex(year, month) < toPayrollPeriodIndex(joining.year, joining.month);
};

const isJoinedForPayrollPeriod = (
  employee: Employee,
  year: string | number,
  month: string | number
) => !isBeforeJoiningMonth(year, month, employee.hire_date);

const payrollPeriodLabel = (year: string | number, month: string | number) =>
  `${MONTH_LABELS[Number(month) - 1] || month} ${year}`;

const joiningPeriodLabel = (hireDate?: string | null) => {
  const joining = hireDateParts(hireDate);
  return joining ? payrollPeriodLabel(joining.year, joining.month) : "the joining month";
};

const employeeDisplayName = (employee?: Employee) =>
  employee
    ? `${employee.first_name || ""}${employee.last_name ? " " + employee.last_name : ""}`.trim() ||
      employee.employeeID ||
      "This employee"
    : "This employee";


const isActiveEmployee = (employee: Employee) => {
  const employmentStatus = (employee.employment_status || "active").toLowerCase();
  return employee.is_active !== false && employmentStatus === "active";
};

const activeEmployeesOnly = (employees: Employee[]) => employees.filter(isActiveEmployee);

const PayrollLoadingState = ({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) => (
  <div
    role="status"
    aria-label={label}
    className={`flex flex-col items-center justify-center text-center ${
      compact ? "gap-3 px-6 py-8" : "gap-5 px-6 py-12"
    }`}
  >
    <div className="relative h-16 w-16">
      <div className="absolute inset-0 rounded-2xl bg-[#0f2d1f]/10" />
      <div className="absolute inset-2 rounded-xl bg-white shadow-sm" />
      <div className="absolute inset-0 rounded-2xl border border-[#0f2d1f]/15" />
      <div className="absolute left-1/2 top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#0f2d1f]/15 border-t-[#0f2d1f] animate-spin" />
      <IndianRupee className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-[#0f2d1f]" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      <div className="mt-3 flex w-48 items-center gap-2">
        <span className="h-1.5 flex-1 rounded-full bg-[#0f2d1f]/20 animate-pulse" />
        <span className="h-1.5 flex-[1.4] rounded-full bg-[#0f2d1f]/30 animate-pulse [animation-delay:150ms]" />
        <span className="h-1.5 flex-[0.8] rounded-full bg-[#0f2d1f]/20 animate-pulse [animation-delay:300ms]" />
      </div>
    </div>
  </div>
);

// A "pick one" toggle rendered as a single track with the chosen segment filled.
// The toolbar carries two of these (Calculation method, Rate type); as loose
// pills they read as one undifferentiated row of buttons, so the shared track
// plus a caption is what tells them apart. `value` may be null — the Rate type
// control uses that for a mixed selection, where no segment is current.
// The group has no visible caption — `label` is the accessible name only. The
// shaded track is what conveys the grouping; visible "METHOD"/"RATE" captions
// cost ~85px and pushed the toolbar onto a second row on scaled displays.
// Each option carries its own tooltip instead.
type SegmentOption = {
  value: string;
  label: string;
  title?: string;
  // Flavours of this segment, reached from a ⋮ beside the label. The segment
  // lights up when any of them is the current value; clicking the label
  // itself applies the segment's own value.
  menu?: { value: string; label: string; title?: string }[];
};

const SegmentedControl = ({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: SegmentOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  // Which option's ⋮ menu is open (its value), if any.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openFor) return;
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpenFor(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenFor(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [openFor]);

  const segment = (isActive: boolean) =>
    `whitespace-nowrap py-1.5 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f2d1f]/40 disabled:cursor-not-allowed ${
      isActive
        ? "bg-[#0f2d1f] text-white shadow-sm"
        : disabled
          ? "text-gray-400"
          : "text-[#0f2d1f] hover:bg-white/80"
    }`;

  return (
    <div
      ref={rootRef}
      role="group"
      aria-label={label}
      className={`inline-flex items-center gap-0.5 rounded-lg border p-0.5 transition-opacity ${
        disabled
          ? "border-gray-200 bg-gray-100 opacity-60"
          : "border-[#b8c9bf] bg-[#e7f0eb]"
      }`}
    >
      {options.map((option) => {
        const isActive =
          value === option.value || Boolean(option.menu?.some((m) => m.value === value));
        if (!option.menu) {
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              aria-pressed={isActive}
              title={option.title}
              className={`rounded-md px-2.5 ${segment(isActive)}`}
            >
              {option.label}
            </button>
          );
        }
        const menuOpen = openFor === option.value;
        return (
          <div key={option.value} className="relative flex items-stretch">
            <button
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              aria-pressed={isActive}
              title={option.title}
              className={`rounded-l-md pl-2.5 pr-1.5 ${segment(isActive)}`}
            >
              {option.label}
            </button>
            <button
              type="button"
              aria-label={`${option.label} options`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={disabled}
              onClick={() => setOpenFor(menuOpen ? null : option.value)}
              className={`rounded-r-md px-1 ${segment(isActive)} ${
                isActive ? "border-l border-white/25" : "border-l border-[#b8c9bf]/60"
              }`}
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                aria-label={`${option.label} options`}
                className="absolute left-0 top-full mt-1.5 z-30 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
              >
                {option.menu.map((item) => {
                  const chosen = value === item.value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOpenFor(null);
                        onChange(item.value);
                      }}
                      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <Check
                        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                          chosen ? "text-[#0f2d1f]" : "text-transparent"
                        }`}
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-gray-800">{item.label}</span>
                        {item.title && (
                          <span className="block text-[11px] leading-snug text-gray-500">{item.title}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CircleCheckbox = ({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={disabled ? undefined : onChange}
    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
      disabled
        ? "cursor-not-allowed border-gray-200 bg-gray-100"
        : checked
        ? "border-[#0f2d1f] bg-[#0f2d1f]"
        : "border-gray-400 bg-white hover:border-[#0f2d1f]"
    }`}
  >
    {checked && <Check className={`h-3 w-3 ${disabled ? "text-gray-300" : "text-white"}`} />}
  </button>
);

const PayrollRecord = ({
  headerActions,
  includeCurrentUserInEmployeeList = false,
}: PayrollRecordProps = {}) => {
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  // Manage level gates payroll mutations (switch/revert/adjust/delete/send); View → read-only.
  const { hasAccess, canManage } = useAccess();
  const canPayrollManage = !hasAccess || canManage("payroll-records");

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);

  const defaultPayrollPeriod = useMemo(() => getPreviousPayrollPeriod(), []);
  const [selectedYear, setSelectedYear] = useState(defaultPayrollPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(defaultPayrollPeriod.month);
  const [selectedDept, setSelectedDept] = useState<string>(ALL_DEPTS);
  const [selectedEmp, setSelectedEmp] = useState<string>(ALL_EMPS);
  const [draftYear, setDraftYear] = useState(defaultPayrollPeriod.year);
  const [draftMonth, setDraftMonth] = useState(defaultPayrollPeriod.month);
  const [draftDept, setDraftDept] = useState<string>(ALL_DEPTS);
  const [draftEmp, setDraftEmp] = useState<string>(ALL_EMPS);

  const [isLoading, setIsLoading] = useState(false);
  const [salaryRecords, setSalaryRecords] = useState<SalaryRecord[]>([]);
  // Employee ids (record.employee_id) currently selected via row checkboxes.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // employee_id -> calculation method last applied to that row.
  const [methodById, setMethodById] = useState<Record<string, MethodKey>>({});
  // employee_id -> persisted Hourly/Monthly override. Employees absent from this
  // map have no override and follow `defaultRateType` (the company setting).
  const [rateTypeById, setRateTypeById] = useState<Record<string, RateType>>({});
  const [defaultRateType, setDefaultRateType] = useState<RateType>("monthly");
  // employee_id -> authoritative approval status (from get_approved_salary, which
  // returns only sent/approved rows). Overlays the per-row draft flags so the
  // Status column can show "Approved" even after the page regenerates a draft.
  const [statusById, setStatusById] = useState<Record<string, StatusKey>>({});
  // employee_id -> amount already sitting in the approval queue (from get_approved_salary).
  const [pendingById, setPendingById] = useState<Record<string, number>>({});
  // employee_id -> the sent/approved SNAPSHOT figures. `net` already includes
  // bonus + extra pay. Overlaid onto the table so an approved employee's row
  // shows the bonus / extra that were actually approved instead of a freshly
  // recalculated draft (which resets them to 0).
  const [approvedFiguresById, setApprovedFiguresById] = useState<
    Record<string, { net: number; bonus: number; extrapay: number }>
  >({});
  const [activeMethod, setActiveMethod] = useState<MethodKey>("attendance");
  // Re-send warning: employees whose salary is ALREADY pending approval.
  const [resendConflicts, setResendConflicts] = useState<
    { id: string; name: string; pending: number; current: number }[]
  >([]);

  const [dailyData, setDailyData] = useState<DailyBreakdown[]>([]);
  const [detailsTab, setDetailsTab] = useState<"daily" | "deductions">("daily");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SalaryRecord | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuPlacement, setMenuPlacement] = useState<"above" | "below">("above");
  const [adjustmentData, setAdjustmentData] = useState({
    bonus: 0,
    extrapay: 0,
    description: "",
  });
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [page, setPage] = useState(1);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Build the year list around the current year so the default year is always selectable.
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));

  const monthLabel = (m: string) => months.find((mm) => mm.value === m)?.label || m;

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  // Hourly/Monthly: the company-wide default plus any per-employee overrides.
  // Drives the Rate Type badge; a failure just leaves every row on the default.
  useEffect(() => {
    salaryApi
      .getEmployeeRateTypes()
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;
        setDefaultRateType(data.default === "hourly" ? "hourly" : "monthly");
        setRateTypeById(data.overrides || {});
      })
      .catch(() => {
        /* keep the monthly default — the badge is informational */
      });
  }, []);

  // Close row menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Portal the action menu to the viewport so table overflow cannot clip it.
  // Close it if either container scrolls or the viewport changes, because its
  // anchor button has moved.
  useEffect(() => {
    if (!openMenuId) return;
    const closeMenu = () => setOpenMenuId(null);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [openMenuId]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);
        const response = await api.get("/employeesAll", {
          params: { active_only: true },
        });
        let nextEmployees = activeEmployeesOnly(response.data.employees || []);

        if (includeCurrentUserInEmployeeList) {
          try {
            const selfProfile = await getMyEmployeeProfile();
            nextEmployees = activeEmployeesOnly(normaliseEmployeeList(nextEmployees, selfProfile));
          } catch {
            /* Self row is best-effort; keep the role-scoped employee list. */
          }
        }

        setEmployees(nextEmployees);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, [includeCurrentUserInEmployeeList]);

  // Format Department name
  const formatDepartment = (dept?: string): string => {
    if (!dept) return "General";
    return dept
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Merge freshly-calculated records into salaryRecords by employee_id (keep others).
  const mergeRecords = (incoming: SalaryRecord[]) => {
    setSalaryRecords((prev) => {
      const byId = new Map<string, SalaryRecord>();
      prev.forEach((r) => byId.set(r.employee_id, r));
      incoming.forEach((r) => byId.set(r.employee_id, r));
      return Array.from(byId.values());
    });
  };

  // AUTO-RUN attendance calc for ALL employees once they load and whenever the period changes.
  useEffect(() => {
    if (employeesLoading || employees.length === 0) return;
    const allIds = employees
      .filter((employee) => isJoinedForPayrollPeriod(employee, selectedYear, selectedMonth))
      .map((e) => e.id);
    runBulk(selectedYear, selectedMonth, allIds, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesLoading, employees, selectedYear, selectedMonth]);

  // Pull the authoritative approval status per employee for the period.
  // get_approved_salary returns only sent/approved rows, so an approved salary
  // stays "Approved" even after the page regenerates a fresh draft.
  // `preservePending` keeps the queued amounts as they were. A recalculation
  // OVERWRITES the same salary row server-side, so re-reading it would report the
  // brand-new figure as the "pending" one — and the re-send warning would compare
  // a value against itself ("Pending ₹4,440 → Latest ₹4,440"). The queued amount
  // must stay at what was actually sent for approval.
  const refreshStatuses = async (
    year: string,
    month: string,
    { preservePending = false }: { preservePending?: boolean } = {},
  ) => {
    try {
      const res = await salaryApi.getApprovedSalaries(year, month);
      const map = buildApprovalStatusMap((res.data?.results as any[]) || []);
      const amounts: Record<string, number> = {};
      const figures: Record<string, { net: number; bonus: number; extrapay: number }> = {};
      ((res.data?.results as any[]) || []).forEach((r) => {
        const d = r?.data || r;
        if (d?.emptID) {
          // Snapshot netSalary ALREADY includes bonus + extra pay, so it IS the
          // queued amount — adding them again counted them twice and inflated
          // the "already queued for ₹X" figure in the re-send warning.
          amounts[d.emptID] = d.netSalary || 0;
          figures[d.emptID] = {
            net: d.netSalary || 0,
            bonus: d.bonus || 0,
            extrapay: d.extrapay || 0,
          };
        }
      });
      setStatusById(map);
      if (!preservePending) setPendingById(amounts);
      setApprovedFiguresById(figures);
    } catch {
      /* best-effort; fall back to per-row flags */
    }
  };

  // Run attendance-based bulk calc; replace records + select all + clear no-deduction flags.
  //
  // This is the page's OWN refresh, not a calculation anybody asked for, so it
  // does not pass `overrideSaved`: rows already sent for approval come back
  // exactly as they were sent, and a row saved as No Deduction keeps that mode.
  // Before that, opening this page silently re-ran every pending row against
  // attendance — the approver's figures, and the employee's payslip, changing
  // with nobody having pressed a thing.
  const runBulk = async (year: string, month: string, ids: string[], selectAll: boolean) => {
    if (!ids.length) {
      setSalaryRecords([]);
      setSelectedIds(new Set());
      setMethodById({});
      setStatusById({});
      setPendingById({});
      setApprovedFiguresById({});
      setActiveMethod("attendance");
      setPage(1);
      return;
    }
    setIsLoading(true);
    try {
      const response = await salaryApi.calculateBulk(year, month, ids);
      const records: SalaryRecord[] = filterOkResults(response.data.results);
      setSalaryRecords(records);
      // Not blanket "attendance" any more: this run leaves a sent row, and a
      // row saved as No Deduction, on the method they already carry, and the
      // server says so on each record. Stamping them all as attendance would
      // label them with a calculation that did not produce them.
      setMethodById(
        Object.fromEntries(
          records.map((r) => [
            r.employee_id,
            ((r.method && SERVER_METHOD[r.method]) || "attendance") as MethodKey,
          ])
        )
      );
      setActiveMethod("attendance");
      if (selectAll) {
        setSelectedIds(new Set(records.map((r) => r.employee_id)));
      }
      setPage(1);
      refreshStatuses(year, month);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || "Failed to calculate salary";
      toast.error(errorMsg);
      setSalaryRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Pick the calc endpoint for a given method. Every one of these is the admin
  // choosing a calculation, so they pass `overrideSaved`: unlike the automatic
  // refresh above, they are allowed to rewrite a row that is already sitting in
  // the approval queue (the re-send warning then shows Pending vs Latest).
  const calcApiFor = (method: MethodKey, ids: string[]) => {
    if (method === "noded")
      return salaryApi.calculateNoDeduction(selectedYear, selectedMonth, ids);
    if (method === "noded_att")
      return salaryApi.calculateNoDeductionWithAttendance(selectedYear, selectedMonth, ids);
    if (method === "system")
      return salaryApi.calculateWithSystemTracking(selectedYear, selectedMonth, ids, true);
    return salaryApi.calculateBulk(selectedYear, selectedMonth, ids, true);
  };

  const setMethodFor = (ids: string[], method: MethodKey) =>
    setMethodById((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = method;
      });
      return next;
    });

  // Toolbar: recalculate the SELECTED rows with one of the three methods
  // (attendance / no-deduction / system-level) and merge the results back.
  const recalcSelected = async (method: MethodKey) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setIsLoading(true);
    try {
      const response = await calcApiFor(method, ids);
      const records: SalaryRecord[] = filterOkResults(response.data.results);
      mergeRecords(records);
      setMethodFor(records.map((r) => r.employee_id), method);
      setActiveMethod(method);
      // Refresh the approval status, but KEEP the queued amounts: a recalc
      // overwrites the same salary row, so re-reading it would make "Pending"
      // equal the figure we just computed and the re-send warning would show
      // the same number on both sides.
      await refreshStatuses(selectedYear, selectedMonth, { preservePending: true });
      toast.success(`Recalculated ${records.length} employee(s) using ${METHOD_META[method].label}.`);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Recalculation failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Persist an Hourly/Monthly choice for the given employees, then recompute
  // those rows so the figures on screen reflect the new basis. `null` clears the
  // override so they follow the company setting again.
  //
  // Each row is recomputed with the calculation method it ALREADY uses, so this
  // never silently moves an employee between Attendance / Workflow / No
  // Deduction — only the hourly-vs-monthly basis changes.
  const applyRateType = async (ids: string[], rateType: RateType | null) => {
    if (!ids.length) return;
    setIsLoading(true);
    try {
      await salaryApi.setEmployeeRateType(ids, rateType);
      setRateTypeById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (rateType === null) delete next[id];
          else next[id] = rateType;
        });
        return next;
      });

      const groups = new Map<MethodKey, string[]>();
      ids.forEach((id) => {
        const record = salaryRecords.find((r) => r.employee_id === id);
        const method: MethodKey = record ? methodOf(record) : "attendance";
        groups.set(method, [...(groups.get(method) ?? []), id]);
      });
      for (const [method, group] of Array.from(groups.entries())) {
        const response = await calcApiFor(method, group);
        mergeRecords(filterOkResults(response.data.results));
      }
      await refreshStatuses(selectedYear, selectedMonth, { preservePending: true });

      toast.success(
        rateType === null
          ? `${ids.length} employee(s) now follow the company setting (${RATE_TYPE_META[defaultRateType].label}).`
          : `${ids.length} employee(s) set to ${RATE_TYPE_META[rateType].label}.`
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Could not update rate type");
    } finally {
      setIsLoading(false);
    }
  };

  // Toolbar: apply a rate type to the SELECTED rows.
  const applyRateTypeToSelected = (rateType: RateType) =>
    applyRateType(Array.from(selectedIds), rateType);

  // Toolbar: Send selected rows for approval (bell notification to each employee).
  // Actually send: notify employees + mark sent. Used directly and after the
  // "Update to Latest" confirmation in the re-send warning.
  const doSendForApproval = async (ids: string[]) => {
    if (!ids.length) return;
    setResendConflicts([]);
    setIsLoading(true);
    try {
      const response = await salaryApi.sendForApproval(selectedYear, selectedMonth, ids);
      const successfulIds = successfulSendForApprovalIds(response, ids);
      const sent = response?.data?.sent_count ?? successfulIds.length;
      // Reflect the new state in the Status column immediately.
      const sentSet = new Set(successfulIds);
      setSalaryRecords((prev) =>
        prev.map((r) =>
          sentSet.has(r.employee_id) ? { ...r, approved: false, sent_for_approval: true } : r
        )
      );
      setStatusById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = statusAfterSendForApproval(next[id]);
        });
        return next;
      });
      // Remember what was ACTUALLY queued. This is the only moment the sent
      // amount is known — the salary row is overwritten by any later recalc, so
      // re-reading it afterwards can no longer tell us what the approver saw.
      // Without this the re-send warning falls back to the current figure and
      // shows "Pending X → Latest X".
      setPendingById((prev) => {
        const next = { ...prev };
        successfulIds.forEach((id) => {
          const rec = salaryRecords.find((r) => r.employee_id === id);
          if (rec) next[id] = rec.netSalary || 0;
        });
        return next;
      });
      toast.success(
        `Salary for ${monthLabel(selectedMonth)} ${selectedYear} sent for approval — ${sent} employee(s) notified.`
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to send for approval");
    } finally {
      setIsLoading(false);
    }
  };

  // Send button: if any selected salary is ALREADY pending approval, warn first
  // (show what's already queued) and let the admin cancel or update to latest.
  const statusForEmployeeId = (id: string): StatusKey => {
    const record = salaryRecords.find((r) => r.employee_id === id);
    return statusById[id] ?? (record ? statusOf(record) : "pending");
  };

  const sendableApprovalIds = (ids: string[]) =>
    ids.filter((id) => statusForEmployeeId(id) !== "approved");

  const handleSendForApproval = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const sendableIds = sendableApprovalIds(ids);
    if (!sendableIds.length) {
      toast.error("Approved salaries cannot be sent for approval again.");
      return;
    }
    const conflictIds = sendableIds.filter((id) => statusForEmployeeId(id) === "sent");
    if (conflictIds.length) {
      setResendConflicts(
        conflictIds.map((id) => {
          const rec = salaryRecords.find((r) => r.employee_id === id);
          // netSalary already carries bonus + extra pay — it IS the amount that
          // would be sent. Re-adding them showed a doubled "current" figure.
          const current = rec ? rec.netSalary || 0 : 0;
          return { id, name: rec?.name || id, pending: pendingById[id] ?? current, current };
        })
      );
      return; // modal opens off resendConflicts
    }
    doSendForApproval(sendableIds);
  };

  const handleApplyFilters = () => {
    setSelectedDept(draftDept);
    setSelectedEmp(draftEmp);
    setSelectedYear(draftYear);
    setSelectedMonth(draftMonth);
    setPage(1);
  };

  // Manual Salary Adjustment Handler
  const handleAdjustSalary = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setAdjustmentData({
      bonus: record.bonus || 0,
      extrapay: record.extrapay || 0,
      description: "",
    });
    setShowAdjustDialog(true);
    setOpenMenuId(null);
  };

  // Submit Salary Adjustment
  const handleSubmitAdjustment = async () => {
    if (!selectedRecord) return;

    // An already-approved salary is locked — never let an adjustment through.
    const currentStatus = statusById[selectedRecord.employee_id] ?? statusOf(selectedRecord);
    if (currentStatus === "approved") {
      toast.error("This salary is already approved and can’t be adjusted.");
      return;
    }

    setIsAdjusting(true);
    try {
      await salaryApi.manualSalaryAdjustment({
        employee_id: selectedRecord.employee_id,
        bonus: adjustmentData.bonus,
        extrapay: adjustmentData.extrapay,
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
        description: adjustmentData.description || "Manual adjustment",
      });

      toast.success(`Salary adjustment applied successfully for ${selectedRecord.name}!`);
      setShowAdjustDialog(false);

      // Recalculate this employee (using whichever method its row currently
      // uses) to pull the updated bonus / extra pay and merge back.
      const method =
        methodById[selectedRecord.employee_id] ||
        (selectedRecord.status === "Manually" ? "noded" : "attendance");
      const response = await calcApiFor(method, [selectedRecord.employee_id]);
      mergeRecords(filterOkResults(response.data.results));

      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
      setSelectedRecord(null);
    } catch (error: any) {
      console.error("Failed to adjust salary:", error);
      toast.error(error?.response?.data?.detail || "Failed to adjust salary");
    } finally {
      setIsAdjusting(false);
    }
  };

  // Fetch daily breakdown data only when the details modal is opened.
  const handleViewMore = async (record: SalaryRecord) => {
    setDailyData([]);
    setDetailsTab("daily");
    setSelectedRecord(record);
    setShowDialog(true);
    setOpenMenuId(null);
    setDetailsLoading(true);

    try {
      const response = await salaryApi.getCalculationLogs(record.salary_id);
      setDailyData(response.data?.attendance || []);
    } catch (error: any) {
      console.error("Failed to fetch salary calculation logs:", error);
      toast.error(error?.response?.data?.detail || "Failed to load attendance details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePreviewSalarySlip = async (record: SalaryRecord) => {
    try {
      await viewSalarySlip(record.employee_id, Number(record.month), Number(record.year));
    } catch (error: any) {
      toast.error(error?.message || "Failed to open salary-slip PDF");
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleDownloadSalarySlip = async (record: SalaryRecord) => {
    try {
      await downloadSalarySlip(record.employee_id, Number(record.month), Number(record.year));
    } catch (error: any) {
      toast.error(error?.message || "Failed to download salary-slip PDF");
    } finally {
      setOpenMenuId(null);
    }
  };

  // Delete handler (client-side removal from the current record set)
  const handleDelete = (record: SalaryRecord) => {
    setConfirm({
      title: `Delete salary record for ${record.name}?`,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        setSalaryRecords((prev) => prev.filter((r) => r.employee_id !== record.employee_id));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(record.employee_id);
          return next;
        });
        toast.success("Salary record removed from view.");
        setOpenMenuId(null);
      },
    });
  };

  // Department + Employee filters apply to which rows are DISPLAYED (client-side).
  const filteredRecords = useMemo(() => {
    return salaryRecords.filter(
      (r) =>
        (selectedDept === ALL_DEPTS || r.department === selectedDept) &&
        (selectedEmp === ALL_EMPS || r.employee_id === selectedEmp)
    );
  }, [salaryRecords, selectedDept, selectedEmp]);

  // Reset to page 1 whenever the filtered set changes shape.
  useEffect(() => {
    setPage(1);
  }, [selectedDept, selectedEmp, salaryRecords.length]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRecords = filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Selection helpers operate on the filtered set (across pages), EXCLUDING
  // approved rows — those are locked, so they can't be selected and don't count
  // toward "Select All" or the selected tally.
  const selectableIds = useMemo(
    () =>
      filteredRecords
        .filter((r) => (statusById[r.employee_id] ?? statusOf(r)) !== "approved")
        .map((r) => r.employee_id),
    [filteredRecords, statusById]
  );
  const allFilteredSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const employeePhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    employees.forEach((employee) => {
      if (employee.profile_photo_url) {
        map[employee.id] = employee.profile_photo_url;
      }
    });
    return map;
  }, [employees]);

  const toggleRow = (employeeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Method last applied to a row (defaults from the record status for rows
  // that were computed before any explicit method toggle).
  const methodOf = (record: SalaryRecord): MethodKey =>
    methodById[record.employee_id] ||
    (record.method && SERVER_METHOD[record.method]) ||
    (record.status === "Manually" || record.status === "No Deduction" ? "noded" : "attendance");

  // A row's effective pay basis: its own override when set, else the company
  // setting — so an untouched employee shows whatever Settings says.
  const rateTypeOf = (record: SalaryRecord): RateType =>
    rateTypeById[record.employee_id] ?? defaultRateType;

  // Hourly pays hours x rate and never accrues overtime, so the breakdown's two
  // overtime columns would be a wall of zeros — hide them for hourly employees.
  const detailsHourly = !!selectedRecord && rateTypeOf(selectedRecord) === "hourly";

  // Count only selectable (non-approved) rows that are selected, so the tally
  // matches what "Select All" can actually pick and never includes a locked row.
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  // What the ticked rows add up to. Uses the same figure as the table's Total
  // column — `netSalary` already has bonus + extra pay baked in (approved rows
  // read from their snapshot), so the number always reconciles with the screen.
  const selectedTotal = useMemo(
    () =>
      filteredRecords
        .filter((r) => selectedIds.has(r.employee_id))
        .reduce((sum, r) => {
          const approvedFig = approvedFiguresById[r.employee_id];
          const isApproved = (statusById[r.employee_id] ?? statusOf(r)) === "approved";
          return sum + (isApproved && approvedFig ? approvedFig.net : r.netSalary || 0);
        }, 0),
    [filteredRecords, selectedIds, approvedFiguresById, statusById]
  );
  const mutationsDisabled = isLoading || selectedCount === 0;
  const filtersDirty =
    draftDept !== selectedDept ||
    draftEmp !== selectedEmp ||
    draftYear !== selectedYear ||
    draftMonth !== selectedMonth;
  // Adjustment dialog: an already-approved salary is locked. Surface the approved
  // (set) amount from the approval queue and disable Apply so it can't be
  // re-adjusted from here.
  const adjustStatus: StatusKey = selectedRecord
    ? statusById[selectedRecord.employee_id] ?? statusOf(selectedRecord)
    : "pending";
  const isAdjustApproved = adjustStatus === "approved";
  // Approval snapshot for the selected employee (net already includes bonus +
  // extra). Used ONLY for an approved (locked) row, so the dialog shows the
  // bonus / extra pay that were actually approved. A sent-but-not-approved row
  // is still recalculable, so its live record wins — same rule as the table.
  const adjustSnapshot = selectedRecord && isAdjustApproved
    ? approvedFiguresById[selectedRecord.employee_id]
    : undefined;
  // Bonus / extra shown in the dialog: the approved snapshot for locked rows,
  // the live input values while editing.
  const adjustShownBonus = isAdjustApproved ? (adjustSnapshot?.bonus ?? 0) : adjustmentData.bonus;
  const adjustShownExtra = isAdjustApproved ? (adjustSnapshot?.extrapay ?? 0) : adjustmentData.extrapay;
  // Calculated (base) net BEFORE bonus/extra. The stored net already bakes them
  // in, so back them out for the "Net Salary" line — otherwise "New Net Salary"
  // (base + bonus + extra) would count bonus/extra twice. Mirrors the backend,
  // which recomputes new net as (netSalary − currentBonus − currentExtra) + new.
  const adjustBaseNet = adjustSnapshot
    ? adjustSnapshot.net - adjustSnapshot.bonus - adjustSnapshot.extrapay
    : (selectedRecord?.netSalary || 0) - (selectedRecord?.bonus || 0) - (selectedRecord?.extrapay || 0);
  const adjustNewNet = adjustBaseNet + adjustShownBonus + adjustShownExtra;
  const selectedEmployeeRecord = employees.find((employee) => employee.id === selectedEmp);
  const selectedEmployeeNotJoined =
    selectedEmp !== ALL_EMPS &&
    isBeforeJoiningMonth(selectedYear, selectedMonth, selectedEmployeeRecord?.hire_date);
  const employeesBeforeJoinCount = employees.filter((employee) =>
    isBeforeJoiningMonth(selectedYear, selectedMonth, employee.hire_date)
  ).length;
  const emptyTitle = selectedEmployeeNotJoined ? "Not joined for this period" : "No Salary Records";
  const emptyMessage = selectedEmployeeNotJoined
    ? `${employeeDisplayName(selectedEmployeeRecord)} had not joined the organisation for ${payrollPeriodLabel(
        selectedYear,
        selectedMonth
      )}. Payroll records are available from ${joiningPeriodLabel(
        selectedEmployeeRecord?.hire_date
      )} onward.`
    : isLoading
    ? "Calculating salary..."
    : employees.length > 0 && employeesBeforeJoinCount === employees.length
    ? `No employees had joined the organisation for ${payrollPeriodLabel(selectedYear, selectedMonth)}.`
    : "Change the filters to refresh salary records.";
  // The rate type shared by every selected row, or null when the selection is
  // empty or mixed. Lets the Hourly/Monthly pair show the selection's current
  // state instead of reading as a blind action.
  const selectedRateType = useMemo<RateType | null>(() => {
    const ids = filteredRecords
      .filter((r) => selectedIds.has(r.employee_id))
      .map((r) => r.employee_id);
    if (!ids.length) return null;
    const first = rateTypeById[ids[0]] ?? defaultRateType;
    return ids.every((id) => (rateTypeById[id] ?? defaultRateType) === first)
      ? first
      : null;
  }, [filteredRecords, selectedIds, rateTypeById, defaultRateType]);
  const HeaderLabel = ({
    first,
    second,
    align = "left",
    testId,
  }: {
    first: string;
    second?: string;
    align?: "left" | "right";
    testId?: string;
  }) => (
    <span
      data-testid={testId}
      aria-label={`${first}${second ? ` ${second}` : ""}`}
      className={`inline-flex flex-col leading-tight ${
        align === "right" ? "items-end text-right" : "items-start"
      }`}
    >
      <span>{first}</span>
      {second && <span>{second}</span>}
    </span>
  );

  if (employeesLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white shadow rounded-2xl max-w-md w-full">
          <PayrollLoadingState label="Loading active employees" />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="payroll-record-page"
      className="min-h-[calc(100vh-64px)] bg-gray-50 p-4"
    >
      <div
        data-testid="payroll-record-card"
        className="mx-auto flex w-full max-w-[96rem] flex-col overflow-hidden rounded-2xl bg-white shadow"
      >
          <div
            data-testid="payroll-record-controls"
            className="shrink-0 rounded-t-2xl bg-white shadow-[0_10px_28px_-24px_rgba(15,45,31,0.65)]"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-200">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="text-xl font-bold text-gray-800 mb-1">Payroll Records</h1>
                  <p className="text-sm text-gray-500 max-w-3xl">
                    Salary is calculated based on daily attendance records, including present days,
                    absences, overtime hours, and applicable deductions. This method uses real-time
                    attendance data from the system.
                  </p>
                </div>
                {headerActions && (
                  <div className="shrink-0 sm:pt-1">
                    {headerActions}
                  </div>
                )}
              </div>
            </div>

            {/* Filter row */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col w-56">
                  <label htmlFor="payroll-record-department" className="text-xs font-semibold text-gray-600 mb-1">Department</label>
                  <FilterDropdown
                    value={draftDept}
                    onChange={setDraftDept}
                    ariaLabel="Filter by department"
                    options={[
                      { value: ALL_DEPTS, label: "All Departments" },
                      ...departmentList.map((dept: any) => ({ value: String(dept.id), label: formatDepartment(dept.name) })),
                    ]}
                  />
                </div>

                <div className="flex flex-col w-56">
                  <label htmlFor="payroll-record-employee" className="text-xs font-semibold text-gray-600 mb-1">Employee</label>
                  <FilterDropdown
                    value={draftEmp}
                    onChange={setDraftEmp}
                    ariaLabel="Filter by employee"
                    options={[
                      { value: ALL_EMPS, label: "All Employees" },
                      ...employees.map((emp) => ({ value: String(emp.id), label: `${emp.first_name}${emp.last_name ? " " + emp.last_name : ""}${emp.employeeID ? ` (${emp.employeeID})` : ""}` })),
                    ]}
                  />
                </div>

                <div className="flex flex-col w-24">
                  <label htmlFor="payroll-record-year" className="text-xs font-semibold text-gray-600 mb-1">Year</label>
                  <FilterDropdown value={draftYear} onChange={setDraftYear} searchable={false} ariaLabel="Filter by year" options={years} />
                </div>

                <div className="flex flex-col w-32">
                  <label htmlFor="payroll-record-month" className="text-xs font-semibold text-gray-600 mb-1">Month</label>
                  <FilterDropdown value={draftMonth} onChange={setDraftMonth} searchable={false} ariaLabel="Filter by month" options={months} />
                </div>

                <button
                  type="button"
                  onClick={handleApplyFilters}
                  disabled={isLoading || employeesLoading || !filtersDirty}
                  aria-label="Apply"
                  title={filtersDirty ? "Apply filter changes" : "Change a filter to enable Apply"}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                    filtersDirty && !isLoading && !employeesLoading
                      ? "bg-[#0f2d1f] text-white shadow-sm hover:bg-[#123b29] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f2d1f]/35"
                      : "bg-[#0f2d1f] text-white cursor-not-allowed opacity-35"
                  }`}
                >
                  <FilterIcon className="w-4 h-4" />
                  Apply
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-white">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSelectAll}
                  disabled={filteredRecords.length === 0}
                  className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
                {/* Kept terse so the whole toolbar stays on ONE line — the full
                    wording lives in the tooltips. The action controls opposite
                    need ~575px, and spelling these out pushed them onto a
                    second row on narrower / scaled displays. */}
                <span
                  data-testid="payroll-selection-count"
                  className="text-xs text-gray-500 whitespace-nowrap"
                  title={`${selectedCount} selected of ${filteredRecords.length} shown`}
                >
                  {selectedCount} / {filteredRecords.length}
                </span>
                {selectedCount > 0 && (
                  // "Payable:" stays — without it this is a bare rupee figure
                  // indistinguishable from a row's Total Salary.
                  <span
                    className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 whitespace-nowrap"
                    title="Total payable for the selected employees"
                  >
                    Payable: ₹{Math.round(selectedTotal).toLocaleString()}
                  </span>
                )}
              </div>

              {canPayrollManage && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                  {/* Recalculate the selected rows with one of the three methods.
                      Applies to this run only. */}
                  <SegmentedControl
                    label="Calculation method"
                    disabled={mutationsDisabled}
                    value={activeMethod}
                    onChange={(value) => recalcSelected(value as MethodKey)}
                    options={[
                      {
                        value: "attendance",
                        label: "Attendance",
                        title: "Calculate from clocked attendance",
                      },
                      {
                        value: "system",
                        label: "Workflow",
                        title: "Calculate from system activity tracking",
                      },
                      {
                        // One segment, two flavours behind its ⋮. Clicking the
                        // label runs the plain one, as it always did.
                        value: "noded",
                        label: "No Deduction",
                        title: "Pay the full salary; attendance is bypassed and the month's days are shown",
                        menu: [
                          {
                            value: "noded",
                            label: "No Deduction",
                            title: "Attendance is bypassed; the month's days are shown as worked.",
                          },
                          {
                            value: "noded_att",
                            label: "No Deduction + Attendance",
                            title: "Full salary plus overtime; the clocked attendance stays on the record.",
                          },
                        ],
                      },
                    ]}
                  />
                  {/* Pay basis. Unlike the method above, this is SAVED per
                      employee and reused for future months. */}
                  <SegmentedControl
                    label="Rate type"
                    disabled={mutationsDisabled}
                    value={selectedRateType}
                    onChange={(value) => applyRateTypeToSelected(value as RateType)}
                    options={[
                      {
                        value: "hourly",
                        label: "Hourly",
                        title: "Pay the selected employees by hours worked (saved for future months)",
                      },
                      {
                        value: "monthly",
                        label: "Monthly",
                        title:
                          "Pay the selected employees a flat per-day share of their monthly salary (saved for future months)",
                      },
                    ]}
                  />
                  <button
                    onClick={handleSendForApproval}
                    disabled={mutationsDisabled}
                    // py-2 + a transparent 1px border makes this exactly as tall
                    // as the segmented tracks beside it (34px), so the toolbar
                    // reads as one clean line.
                    className={`px-3 py-2 border border-transparent text-xs font-semibold rounded-lg ${PAYROLL_GREEN} disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f2d1f]/40`}
                  >
                    <Send className="w-3.5 h-3.5" />
                    Send for Approval
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            data-testid="payroll-table-section"
            className="flex flex-col p-4"
          >
            {isLoading && filteredRecords.length === 0 ? (
              <PayrollLoadingState label="Calculating payroll records" />
            ) : filteredRecords.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <IndianRupee className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-500 mb-1">{emptyTitle}</h3>
                <p className="text-sm text-gray-400">
                  {emptyMessage}
                </p>
              </div>
            ) : (
              <>
                <div
                  data-testid="payroll-table-scroll"
                  className="relative overflow-auto rounded-lg border border-gray-200 bg-white"
                  style={{ maxHeight: "calc(100vh - 140px)" }}
                >
                {isLoading && (
                  <div className="absolute inset-0 z-30 flex items-start justify-center bg-white/80 backdrop-blur-[2px]">
                    <PayrollLoadingState label="Calculating payroll records" compact />
                  </div>
                )}
                <table className="pr-table w-full min-w-[1280px] table-auto">
                  <colgroup>
                    <col style={{ width: "36px" }} />
                    <col style={{ width: "200px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "86px" }} />
                    <col style={{ width: "92px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "166px" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200 text-left">
                      <th className="sticky left-0 top-0 z-30 bg-gray-100 px-3 py-2" />
                      <th className="sticky left-10 top-0 z-30 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide shadow-[8px_0_12px_-12px_rgba(15,23,42,0.45)]">Name</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">Department</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="TakeHome" second="Salary" align="right" testId="payroll-header-takehome-salary" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Calculated" second="Salary" align="right" testId="payroll-header-calculated-salary" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">Bonus</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Extra" second="Pay" align="right" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Total" second="Salary" align="right" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                        <HeaderLabel first="Calculation" second="Method" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                        <HeaderLabel first="Rate" second="Type" testId="payroll-header-rate-type" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {pageRecords.map((record) => {
                      const isMenuOpen = openMenuId === record.employee_id;
                      const isSelected = selectedIds.has(record.employee_id);
                      const methodMeta = METHOD_META[methodOf(record)];
                      const rowRateType = rateTypeOf(record);
                      const rateMeta = RATE_TYPE_META[rowRateType];
                      // No override row → this employee is simply following the
                      // company-wide setting; worth saying so on hover.
                      const followsCompanyRate = rateTypeById[record.employee_id] === undefined;
                      // Hourly only changes the math on the Attendance method —
                      // Workflow and No Deduction ignore it, exactly as the
                      // company-wide setting always has. Say so instead of
                      // showing a badge that looks like it's doing something.
                      const rateApplies = methodOf(record) === "attendance";
                      const rowStatus = statusById[record.employee_id] ?? statusOf(record);
                      const statusMeta = STATUS_META[rowStatus];
                      // An approved salary is locked: the row can't be selected or
                      // edited/deleted — only viewed (see the gated menu below).
                      const isApproved = rowStatus === "approved";
                      const deptName = formatDepartment(
                        departmentList.find((e: any) => e.id === record.department)?.name
                      );
                      // Employee's actual monthly salary (gross) — matches the
                      // employee Payroll page. Avoids the declared-net takeHome
                      // which can go negative (flat LWF) on zero-salary rows.
                      const monthly = record.takeHome || 0;
                      // For APPROVED employees only, show the figures from the
                      // approval snapshot (which carry the bonus / extra pay that
                      // was actually approved) instead of the freshly recalculated
                      // draft, which resets them to 0. Snapshot `net` already
                      // includes bonus + extra, so back them out for the
                      // Calculated column to keep Calculated + Bonus + Extra = Total.
                      //
                      // A merely SENT row must NOT use the snapshot: its salary
                      // row is still mutable, so recalculating it (e.g. switching
                      // Attendance → No Deduction) overwrites it server-side and
                      // the fresh figures are the truth. Overlaying the snapshot
                      // there pinned the table to the numbers captured when the
                      // row was first queued, so a No-Deduction recalc kept
                      // displaying the Attendance amounts.
                      const approvedFig = isApproved
                        ? approvedFiguresById[record.employee_id]
                        : undefined;
                      const bonus = approvedFig ? approvedFig.bonus : (record.bonus || 0);
                      const extra = approvedFig ? approvedFig.extrapay : (record.extrapay || 0);
                      // `netSalary` ALREADY includes bonus + extra pay on BOTH
                      // paths — the backend bakes them in (apply_manual_additions:
                      // adjusted_net = net + bonus + extrapay). So it is the Total,
                      // and the Calculated column is that minus the two. The draft
                      // path used to treat netSalary as the pre-bonus figure and
                      // then add bonus/extra on top, counting them twice.
                      const total = approvedFig ? approvedFig.net : (record.netSalary || 0);
                      const calc = total - bonus - extra;
                      const profilePhotoUrl =
                        record.profile_photo_url || employeePhotoById[record.employee_id] || null;

                      return (
                        <tr
                          key={record.employee_id}
                          className={`group transition-colors ${
                            isApproved ? "bg-gray-50/60 opacity-60" : "hover:bg-gray-50"
                          }`}
                          title={isApproved ? "Approved — locked (view only)" : undefined}
                        >
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 group-hover:bg-gray-50">
                            <CircleCheckbox
                              checked={isSelected}
                              onChange={() => toggleRow(record.employee_id)}
                              label={`Select ${record.name}`}
                              disabled={isApproved}
                            />
                          </td>
                          <td className="sticky left-10 z-10 bg-white px-3 py-2 shadow-[8px_0_12px_-12px_rgba(15,23,42,0.35)] group-hover:bg-gray-50" title={`${record.name}${record.employeeID ? ` (${record.employeeID})` : ""}`}>
                            <div
                              className="flex min-w-0 items-center gap-2"
                              aria-label={`Employee ${record.name}, ID ${record.employeeID || "not available"}`}
                            >
                              <PersonAvatar
                                employeeId={record.employee_id}
                                employeeCode={record.employeeID}
                                photoUrl={profilePhotoUrl}
                                name={record.name}
                                size={32}
                              />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold leading-tight text-gray-900">{record.name}</span>
                                <span className="block truncate text-xs font-medium text-gray-400">{record.employeeID}</span>
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 truncate" title={deptName}>
                            {deptName}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-800 text-right whitespace-nowrap">
                            ₹{monthly.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-800 text-right whitespace-nowrap">
                            ₹{calc.toLocaleString()}
                            {/* Why this figure is lower than the days worked
                                suggest. Without it the row shows a reduced Net
                                with nothing to attribute it to — the question
                                every one of these deductions produces. */}
                            <PayDeductionNote record={record} />
                          </td>
                          <td className="px-3 py-2 text-xs text-amber-600 text-right whitespace-nowrap">
                            ₹{bonus.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-blue-600 text-right whitespace-nowrap">
                            ₹{extra.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs font-bold text-gray-900 text-right whitespace-nowrap">
                            ₹{total.toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex max-w-[108px] whitespace-normal break-words px-2 py-0.5 text-xs font-semibold leading-tight rounded-full ${methodMeta.cls}`}
                            >
                              {methodMeta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              data-testid={`payroll-rate-type-${record.employee_id}`}
                              title={[
                                followsCompanyRate
                                  ? `Following the company setting (${rateMeta.label})`
                                  : `Set to ${rateMeta.label} for this employee`,
                                rateApplies
                                  ? ""
                                  : ` — the ${methodMeta.label} method ignores this`,
                              ].join("")}
                              className={`inline-flex whitespace-nowrap px-2 py-0.5 text-xs font-semibold leading-tight rounded-full ${rateMeta.cls} ${
                                followsCompanyRate || !rateApplies ? "opacity-60" : ""
                              }`}
                            >
                              {rateMeta.label}
                              {!rateApplies && <span className="ml-1 font-normal">·n/a</span>}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            <div className="relative flex items-center justify-start gap-1.5 whitespace-nowrap">
                              <span
                                className={`inline-flex whitespace-nowrap px-2 py-0.5 text-xs font-semibold leading-tight rounded-full ${statusMeta.cls}`}
                              >
                                {statusMeta.label}
                              </span>
                              {isApproved ? (
                                <>
                                  {/* Approved salary is locked → direct read-only actions, no three-dot menu. */}
                                  <button
                                    type="button"
                                    aria-label={`View details for ${record.name}`}
                                    title="View details"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewMore(record);
                                    }}
                                    className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                  >
                                    <Eye className="w-4 h-4 text-gray-500" />
                                  </button>
                                  {canPayrollManage && (
                                    <>
                                      <button
                                        type="button"
                                        aria-label={`View Salary Slip PDF for ${record.name}`}
                                        title="View Salary Slip PDF"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreviewSalarySlip(record);
                                        }}
                                        className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                      >
                                        <FileText className="w-4 h-4 text-gray-500" />
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={`Download Salary Slip PDF for ${record.name}`}
                                        title="Download Salary Slip PDF"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadSalarySlip(record);
                                        }}
                                        className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                      >
                                        <Download className="w-4 h-4 text-gray-500" />
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    aria-label={`Actions for ${record.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isMenuOpen) {
                                        setOpenMenuId(null);
                                        return;
                                      }

                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const menuWidth = 224;
                                      // Manage view also carries the Rate Type
                                      // block: label row + the inline 3-way
                                      // control (~66px). Fixed now that "Company"
                                      // is a segment rather than an extra row.
                                      const menuHeight = canPayrollManage ? 240 : 42;
                                      const rightOffset = 28;
                                      const hasRoomAbove = rect.top >= menuHeight + 12;
                                      setMenuPlacement(hasRoomAbove ? "above" : "below");
                                      setMenuPosition({
                                        // Prefer above; for rows near the top,
                                        // open below so the viewport never cuts
                                        // off the first menu actions.
                                        top: hasRoomAbove ? rect.top - 6 : rect.bottom + 6,
                                        left: Math.min(
                                          window.innerWidth - menuWidth - 8,
                                          Math.max(8, rect.right - menuWidth + rightOffset)
                                        ),
                                      });
                                      setOpenMenuId(record.employee_id);
                                    }}
                                    className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                  >
                                    <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                  </button>

                                  {isMenuOpen && createPortal(
                                    <div
                                      ref={menuRef}
                                      role="menu"
                                      aria-label={`Actions menu for ${record.name}`}
                                      className="fixed w-56 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999] overflow-hidden"
                                      style={{
                                        top: menuPosition.top,
                                        left: menuPosition.left,
                                        transform:
                                          menuPlacement === "above"
                                            ? "translateY(-100%)"
                                            : undefined,
                                      }}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewMore(record);
                                        }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        View Details
                                      </button>
                                      {canPayrollManage && (
                                        <>
                                          <button
                                            type="button"
                                            aria-label={`View Salary Slip PDF for ${record.name}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePreviewSalarySlip(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs border-t border-gray-100"
                                          >
                                            <FileText className="w-3.5 h-3.5" />
                                            View Salary Slip PDF
                                          </button>
                                          <button
                                            type="button"
                                            aria-label={`Download Salary Slip PDF for ${record.name}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadSalarySlip(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs border-t border-gray-100"
                                          >
                                            <Download className="w-3.5 h-3.5" />
                                            Download Salary Slip PDF
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAdjustSalary(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-gray-700 hover:text-blue-600 text-xs border-t border-gray-100"
                                          >
                                            <Save className="w-3.5 h-3.5" />
                                            Adjust Salary
                                          </button>
                                          {/* Pay basis for THIS employee, as one
                                              inline 3-way choice. "Company" is a
                                              real segment rather than an extra row,
                                              so the control always shows the actual
                                              state: an explicit override, or
                                              following the company setting. */}
                                          <div className="border-t border-gray-100 px-3 pt-2 pb-2.5">
                                            <div className="mb-1.5 flex items-center gap-2 text-xs text-gray-700">
                                              <Clock className="w-3.5 h-3.5" />
                                              Rate Type
                                            </div>
                                            <div
                                              role="group"
                                              aria-label={`Rate type for ${record.name}`}
                                              className="flex items-center gap-0.5 rounded-lg border border-[#b8c9bf] bg-[#e7f0eb] p-0.5"
                                            >
                                              {([
                                                { key: "hourly", label: "Hourly", title: "Pay by hours worked" },
                                                { key: "monthly", label: "Monthly", title: "Pay a flat per-day share of the monthly salary" },
                                                {
                                                  key: "company",
                                                  label: "Default",
                                                  title: `Follow the company setting (${RATE_TYPE_META[defaultRateType].label})`,
                                                },
                                              ] as const).map((opt) => {
                                                const isActive = followsCompanyRate
                                                  ? opt.key === "company"
                                                  : opt.key === rowRateType;
                                                return (
                                                  <button
                                                    key={opt.key}
                                                    type="button"
                                                    aria-pressed={isActive}
                                                    title={opt.title}
                                                    aria-label={`Set ${record.name} rate type to ${opt.label}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setOpenMenuId(null);
                                                      applyRateType(
                                                        [record.employee_id],
                                                        opt.key === "company" ? null : (opt.key as RateType)
                                                      );
                                                    }}
                                                    className={`flex-1 rounded-md px-1 py-1 text-[11px] font-semibold transition-all ${
                                                      isActive
                                                        ? "bg-[#0f2d1f] text-white shadow-sm"
                                                        : "text-[#0f2d1f] hover:bg-white/80"
                                                    }`}
                                                  >
                                                    {opt.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-gray-700 hover:text-red-600 text-xs border-t border-gray-100"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete
                                          </button>
                                        </>
                                      )}
                                    </div>,
                                    document.body
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex shrink-0 items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {filteredRecords.length} record(s) total
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-medium text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
      </div>

      {/* Dialog Modal for Attendance Breakdown */}
      {showDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <PersonAvatar
                    employeeId={selectedRecord.employee_id}
                    employeeCode={selectedRecord.employeeID}
                    photoUrl={selectedRecord.profile_photo_url}
                    name={selectedRecord.name}
                    size={40}
                  />
                  <div>
                    <h2 className="text-lg font-bold text-white" style={{ color: "#fff" }}>Daily Attendance Breakdown</h2>
                    <p className="text-xs text-emerald-100">
                      {selectedRecord.name} -{" "}
                      {months.find(
                        (m) => m.value === selectedRecord.month?.toString().padStart(2, "0")
                      )?.label}{" "}
                      {selectedRecord.year}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Beside the title rather than on their own row: the
                      modal is height-constrained and a second header row costs
                      the table two visible days. */}
                  {(selectedRecord.lateComing?.amount || selectedRecord.hoursShortfall?.amount) ? (
                    <div className="flex gap-1">
                      {([
                        ["daily", "Daily Breakdown"],
                        [
                          "deductions",
                          selectedRecord.lateComing?.action === "deduct"
                            ? "Deduction details"
                            : "Hold details",
                        ],
                      ] as const).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setDetailsTab(key)}
                          // Colours inline on purpose: a global stylesheet forces
                          // text colour inside this header — the same reason the
                          // <h2> above carries style={{ color: "#fff" }} — so a
                          // Tailwind text class renders white-on-white and the
                          // label vanishes.
                          style={{
                            color: detailsTab === key ? "#065f46" : "#111827",
                            backgroundColor: detailsTab === key ? "#ffffff" : "#d1fae5",
                          }}
                          className={`whitespace-nowrap rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                            detailsTab === key ? "shadow-sm" : "hover:opacity-90"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <button
                    onClick={() => setShowDialog(false)}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

            </div>

            <div className="flex-1 min-h-0 overflow-auto">
              {detailsTab === "deductions" ? (
                <PayDeductionDetails record={selectedRecord} dailyData={dailyData} />
              ) : detailsLoading ? (
                <div className="text-center py-8" role="status">
                  <p className="text-gray-500 text-sm">Loading attendance details...</p>
                </div>
              ) : dailyData.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No attendance data available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[500px]">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Hours</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Payable</th>
                        {!detailsHourly && (
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Overtime</th>
                        )}
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Day Salary</th>
                        {!detailsHourly && (
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">OT Amount</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dailyData.map((day, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                            {new Date(day.date).toLocaleDateString("en-IN")}
                          </td>
                          {/* "8h 15m", not "8.25h" — see utils/hoursFormat. */}
                          <td className="px-3 py-2 text-gray-700 font-mono">{formatHoursMinutes(day.hours)}</td>
                          <td className="px-3 py-2">
                            {(() => {
                              // Two badges, because a day is two facts: how it
                              // was PAID (Present / Half Day / Leave / Sunday)
                              // and whether it carried overtime.
                              //
                              // This used to render "Overtime" INSTEAD of the
                              // status whenever the day had any overtime — so
                              // the one column that explains a 0.5 in Payable
                              // was hidden. A day worked 11h30m and paid half
                              // read as a plain "Overtime" row with no clue
                              // why. Both now show: overtime keeps its badge,
                              // it just no longer eats the reason next to it.
                              const meta = dayStatusMeta(day.status);
                              const ot = dayStatusMeta("OVERTIME");
                              const hasOt = (day.overtime || 0) > 0;
                              return (
                                <span className="inline-flex flex-wrap items-center gap-1">
                                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.cls}`}>
                                    {meta.label}
                                  </span>
                                  {hasOt && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${ot.cls}`}>
                                      {ot.label}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                          {/* Payable is a DAY count (1 / 0.5 / 0), not hours. */}
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{day.payable}</td>
                          {!detailsHourly && (
                            <td className="px-3 py-2 text-right font-mono text-purple-600">{formatHoursMinutes(day.overtime)}</td>
                          )}
                          <td className="px-3 py-2 text-right font-mono font-semibold text-blue-600">
                            ₹{day.day_salary?.toLocaleString()}
                          </td>
                          {!detailsHourly && (
                            <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">
                              ₹{day.overtime_amount?.toLocaleString()}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="shrink-0 px-5 py-3 bg-gray-50 border-t border-gray-200 space-y-2">
              {(() => {
                const earned = dailyData.reduce(
                  (acc, day) => acc + (day.day_salary || 0) + (day.overtime_amount || 0),
                  0
                );
                const deducted =
                  (selectedRecord.lateComing?.amount || 0) +
                  (selectedRecord.hoursShortfall?.amount || 0);
                return (
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700 text-sm">
                      Total Net Salary:
                    </span>
                    <span className="text-xl font-bold text-emerald-600">
                      {/* Still subtracts. The old total summed the day rows
                          alone, so it disagreed with the Net actually paid. The
                          working behind the difference is on the Hold details
                          tab rather than repeated here. */}
                      ₹{(earned - deducted).toLocaleString()}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Manual Salary Adjustment Dialog */}
      {showAdjustDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ color: "#fff" }}>Manual Salary Adjustment</h2>
                  <p className="text-xs text-blue-100">
                    {selectedRecord.name} -{" "}
                    {months.find(
                      (m) => m.value === selectedRecord.month?.toString().padStart(2, "0")
                    )?.label}{" "}
                    {selectedRecord.year}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAdjustDialog(false);
                    setSelectedRecord(null);
                    setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                  }}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="space-y-3">
                {/* Bonus Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-amber-600" />
                    Bonus Amount
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.bonus || ""}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, bonus: parseInt(e.target.value) || 0 })
                    }
                    min="0"
                    step="100"
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter bonus amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Additional bonus to be added to salary</p>
                </div>

                {/* Extra Pay Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-blue-600" />
                    Extra Pay
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.extrapay || ""}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, extrapay: parseInt(e.target.value) || 0 })
                    }
                    min="0"
                    step="100"
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter extra pay amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Extra payment to be added to salary</p>
                </div>

                {/* Description Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    Description (Optional)
                  </label>
                  <textarea
                    value={adjustmentData.description}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, description: e.target.value })
                    }
                    rows={2}
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm resize-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter reason for adjustment..."
                  />
                </div>

                {/* Current Salary Info */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {isAdjustApproved ? "Approved Net Salary:" : "Current Net Salary:"}
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₹{adjustBaseNet?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Bonus:</span>
                    <span className="font-medium text-amber-600">
                      ₹{adjustShownBonus?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Extra Pay:</span>
                    <span className="font-medium text-blue-600">
                      ₹{adjustShownExtra?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">New Net Salary:</span>
                    <span className="font-bold text-emerald-600">
                      ₹{adjustNewNet?.toLocaleString()}
                    </span>
                  </div>
                </div>

                {isAdjustApproved && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                    This salary is already approved and can’t be adjusted.
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdjustDialog(false);
                      setSelectedRecord(null);
                      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitAdjustment}
                    disabled={isAdjusting || isAdjustApproved}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {isAdjusting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Applying...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Apply Adjustment</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Re-send warning: some selected salaries are already pending approval. */}
      {resendConflicts.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-amber-500 px-5 py-3">
              <h2 className="text-lg font-bold" style={{ color: "#fff" }}>
                Already sent for approval
              </h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-3">
                {resendConflicts.length} salar{resendConflicts.length === 1 ? "y is" : "ies are"} already
                pending approval. Sending again will update {resendConflicts.length === 1 ? "it" : "them"} with
                the latest calculation.
              </p>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg mb-4">
                {resendConflicts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm gap-3">
                    <span className="font-medium text-gray-800 truncate">{c.name}</span>
                    <span className="text-gray-600 whitespace-nowrap">
                      Pending ₹{c.pending.toLocaleString()}
                      {c.current !== c.pending && (
                        <> → Latest ₹{c.current.toLocaleString()}</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setResendConflicts([])}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => doSendForApproval(sendableApprovalIds(Array.from(selectedIds)))}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Update to Latest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default PayrollRecord;
